import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { apiErr } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { CalendarClock, Plus, ChevronLeft, ChevronRight, MoreVertical } from "lucide-react";
import { toast } from "sonner";

const STATUS_STYLE = {
  scheduled: "border-l-primary",
  checked_in: "border-l-accent",
  completed: "border-l-emerald-500",
  no_show: "border-l-destructive",
  cancelled: "border-l-muted-foreground",
};
const STATUS_LABEL = { scheduled: "Scheduled", checked_in: "Checked in", completed: "Completed", no_show: "No-show", cancelled: "Cancelled" };

function dayKey(d) { return new Date(d).toISOString().slice(0, 10); }

export default function Calendar() {
  const qc = useQueryClient();
  const [anchor, setAnchor] = useState(new Date());
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ patient_id: "", practitioner_id: "", start: "", duration_min: 30, room: "" });

  const { data: bookings = [] } = useQuery({ queryKey: ["bookings"], queryFn: async () => (await api.get("/bookings")).data });
  const { data: patients = [] } = useQuery({ queryKey: ["patients"], queryFn: async () => (await api.get("/patients")).data });
  const { data: staff = [] } = useQuery({ queryKey: ["staff"], queryFn: async () => (await api.get("/staff")).data });

  const createMut = useMutation({
    mutationFn: async (body) => (await api.post("/bookings", body)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["bookings"] }); setOpen(false); toast.success("Booking created — confirmation dispatched via n8n"); },
    onError: (e) => toast.error(apiErr(e)),
  });
  const statusMut = useMutation({
    mutationFn: async ({ id, status }) => (await api.put(`/bookings/${id}/status`, { status })).data,
    onSuccess: (_d, v) => { qc.invalidateQueries({ queryKey: ["bookings"] }); toast.success(`Marked ${STATUS_LABEL[v.status]}`); },
    onError: (e) => toast.error(apiErr(e)),
  });

  const shift = (n) => { const d = new Date(anchor); d.setDate(d.getDate() + n); setAnchor(d); };
  const dayBookings = bookings
    .filter((b) => dayKey(b.start) === dayKey(anchor))
    .sort((a, b) => new Date(a.start) - new Date(b.start));

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">Calendar</h1>
          <p className="mt-1 text-sm text-muted-foreground">Bookings, check-ins and no-shows</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="new-booking-btn" className="gap-2 rounded-full"><Plus className="h-4 w-4" /> New booking</Button>
          </DialogTrigger>
          <DialogContent className="bg-popover">
            <DialogHeader><DialogTitle>New booking</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div>
                <Label>Patient</Label>
                <Select value={form.patient_id} onValueChange={(v) => setForm({ ...form, patient_id: v })}>
                  <SelectTrigger data-testid="booking-patient"><SelectValue placeholder="Select patient" /></SelectTrigger>
                  <SelectContent className="bg-popover">
                    {patients.map((p) => <SelectItem key={p.id} value={p.id}>{p.full_name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Practitioner</Label>
                <Select value={form.practitioner_id} onValueChange={(v) => setForm({ ...form, practitioner_id: v })}>
                  <SelectTrigger data-testid="booking-practitioner"><SelectValue placeholder="Select practitioner" /></SelectTrigger>
                  <SelectContent className="bg-popover">
                    {staff.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Start</Label><Input type="datetime-local" data-testid="booking-start" value={form.start} onChange={(e) => setForm({ ...form, start: e.target.value })} /></div>
              <div><Label>Room</Label><Input data-testid="booking-room" value={form.room} onChange={(e) => setForm({ ...form, room: e.target.value })} placeholder="Room 1" /></div>
            </div>
            <DialogFooter>
              <Button
                data-testid="booking-submit"
                className="rounded-full"
                disabled={!form.patient_id || !form.practitioner_id || !form.start || createMut.isPending}
                onClick={() => createMut.mutate({ ...form, start: new Date(form.start).toISOString() })}
              >
                Create booking
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="rounded-2xl border-border/60 p-5">
        <div className="mb-5 flex items-center justify-between">
          <Button variant="ghost" size="icon" data-testid="cal-prev" onClick={() => shift(-1)} className="rounded-full"><ChevronLeft className="h-4 w-4" /></Button>
          <div className="text-center">
            <div className="flex items-center justify-center gap-2 font-semibold"><CalendarClock className="h-4 w-4 text-primary" />
              {anchor.toLocaleDateString("en-ZA", { weekday: "long", day: "numeric", month: "long" })}
            </div>
            <div className="text-xs text-muted-foreground">{dayBookings.length} bookings</div>
          </div>
          <Button variant="ghost" size="icon" data-testid="cal-next" onClick={() => shift(1)} className="rounded-full"><ChevronRight className="h-4 w-4" /></Button>
        </div>

        {dayBookings.length === 0 ? (
          <div className="py-12 text-center text-sm text-muted-foreground">No bookings this day.</div>
        ) : (
          <div className="space-y-2">
            {dayBookings.map((b) => (
              <div key={b.id} data-testid={`booking-${b.id}`} className={`flex items-center gap-4 rounded-xl border border-border/60 border-l-4 bg-card px-4 py-3 ${STATUS_STYLE[b.status]}`}>
                <div className="w-14 text-sm font-semibold text-primary">
                  {new Date(b.start).toLocaleTimeString("en-ZA", { hour: "2-digit", minute: "2-digit" })}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="truncate font-medium">{b.patient_name}</div>
                  <div className="text-xs text-muted-foreground">{b.practitioner_name} · {b.room || "—"}</div>
                </div>
                <Badge variant="outline" className="rounded-full text-[11px]">{STATUS_LABEL[b.status]}</Badge>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" data-testid={`booking-menu-${b.id}`} className="rounded-full"><MoreVertical className="h-4 w-4" /></Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="bg-popover">
                    <DropdownMenuItem data-testid={`status-checked_in-${b.id}`} onClick={() => statusMut.mutate({ id: b.id, status: "checked_in" })}>Check in</DropdownMenuItem>
                    <DropdownMenuItem data-testid={`status-completed-${b.id}`} onClick={() => statusMut.mutate({ id: b.id, status: "completed" })}>Complete</DropdownMenuItem>
                    <DropdownMenuItem data-testid={`status-no_show-${b.id}`} onClick={() => statusMut.mutate({ id: b.id, status: "no_show" })}>No-show</DropdownMenuItem>
                    <DropdownMenuItem data-testid={`status-cancelled-${b.id}`} onClick={() => statusMut.mutate({ id: b.id, status: "cancelled" })}>Cancel</DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
